/**
 * ReportFilters — integrated filter bar + collapsible panel for ProjectReport.
 *
 * ReportFilterBar: inline button row rendered next to TabsList
 * ReportFilterPanel: collapsible panel rendered between TabsList and TabsContent
 */

import { Calendar as CalendarIcon, ChevronDown, Download, History, User, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTesters } from '@/hooks/useTesters';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { STATUS_CONFIG } from '@/lib/status-config';
import { cn } from '@/lib/utils';
import { findTesterNameByEmail } from '@/lib/cognito-mapping';
import type { UseReportFiltersReturn, ScheduleFilter } from '@/hooks/useReportFilters';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ReportFilterBarProps {
  hook: UseReportFiltersReturn;
  isExporting?: boolean;
  onExport: () => void;
  onExportAll?: () => void;
  /** When set, renders a "Previous exports" link pointing to the given path. */
  exportsHistoryPath?: string;
}

interface ReportFilterPanelProps {
  hook: UseReportFiltersReturn;
  issueCount: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SCHEDULE_LABELS: Record<string, string> = {
  overdue: 'Overdue',
  due_this_week: 'Due this week',
  due_next_week: 'Due next week',
  due_this_month: 'Due this month',
  no_due_date: 'No due date',
  custom: 'Custom range',
};

interface ActiveFilter {
  key: string;
  label: string;
  value: string;
  onClear: () => void;
}

function formatMultiValue(selected: string[], maxInline = 2): string {
  if (selected.length === 0) return '';
  if (selected.length <= maxInline) return selected.join(', ');
  return `${selected.length} selected`;
}

function getActiveFilters(hook: UseReportFiltersReturn): ActiveFilter[] {
  const { filters, resetFilters } = hook;
  const active: ActiveFilter[] = [];

  if (filters.domain.length > 0) {
    active.push({
      key: 'domain',
      label: 'Domain',
      value: formatMultiValue(filters.domain),
      onClear: () => {
        // Clear domain by toggling each selected one off
        for (const d of filters.domain) hook.toggleDomainFilter(d);
      },
    });
  }
  if (filters.area.length > 0) {
    active.push({
      key: 'area',
      label: 'Area',
      value: formatMultiValue(filters.area),
      onClear: () => {
        for (const a of filters.area) hook.toggleAreaFilter(a);
      },
    });
  }
  if (filters.tester.length > 0) {
    active.push({
      key: 'tester',
      label: 'Tester',
      value: formatMultiValue(filters.tester),
      onClear: () => {
        for (const t of filters.tester) hook.toggleTesterFilter(t);
      },
    });
  }
  if (filters.schedule !== 'all') {
    active.push({
      key: 'schedule',
      label: 'Schedule',
      value: SCHEDULE_LABELS[filters.schedule] ?? filters.schedule,
      onClear: () => hook.setSchedule('all'),
    });
  }
  if (filters.status.length > 0) {
    const labels = filters.status.map((s) =>
      s === 'unassigned'
        ? 'Unassigned'
        : (STATUS_CONFIG as Record<string, { label: string }>)[s]?.label ?? s,
    );
    active.push({
      key: 'status',
      label: 'Status',
      value: labels.length <= 2 ? labels.join(', ') : `${labels.length} selected`,
      onClear: () => {
        for (const s of filters.status) hook.toggleStatus(s);
      },
    });
  }
  if (filters.hasIssues) {
    active.push({ key: 'issues', label: 'Issues', value: 'With Issues', onClear: () => hook.setHasIssues(false) });
  }

  // Only use resetFilters to suppress lint — it's used in the Clear all button
  void resetFilters;

  return active;
}

function FilterChip({ label, value, onClear }: { label: string; value: string; onClear: () => void }) {
  return (
    <Badge variant="secondary" className="gap-1 pl-2 pr-1 py-0.5 text-xs font-normal max-w-[160px]">
      <span className="text-muted-foreground">{label}:</span>
      <span className="truncate">{value}</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClear(); }}
        className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
      >
        <X className="h-3 w-3" />
      </button>
    </Badge>
  );
}

const SCHEDULE_OPTIONS: { value: ScheduleFilter; label: string }[] = [
  { value: 'all', label: 'All dates' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'due_this_week', label: 'Due this week' },
  { value: 'due_next_week', label: 'Due next week' },
  { value: 'due_this_month', label: 'Due this month' },
  { value: 'no_due_date', label: 'No due date' },
  { value: 'custom', label: 'Custom range...' },
];

// ─── ReportFilterBar ─────────────────────────────────────────────────────────

export function ReportFilterBar({ hook, isExporting, onExport, onExportAll, exportsHistoryPath }: ReportFilterBarProps) {
  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      {hook.isFiltered && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 text-muted-foreground"
          onClick={hook.resetFilters}
        >
          <X className="h-3.5 w-3.5" />
          Clear filters
        </Button>
      )}

      {/* Export: dropdown with "Export All" only when filters are active */}
      {hook.isFiltered && onExportAll ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={isExporting}>
              <Download className="h-3.5 w-3.5" />
              {isExporting ? 'Exporting...' : 'Export'}
              <ChevronDown className="h-3 w-3 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExport}>
              Export Filtered
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExportAll}>
              Export All
            </DropdownMenuItem>
            {exportsHistoryPath && (
              <DropdownMenuItem asChild>
                <Link to={exportsHistoryPath} className="flex items-center gap-2">
                  <History className="h-3.5 w-3.5" />
                  Previous exports
                </Link>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={onExport} disabled={isExporting}>
            <Download className="h-3.5 w-3.5" />
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
          {exportsHistoryPath && (
            <Button asChild variant="ghost" size="sm" className="h-8 gap-1.5 text-muted-foreground">
              <Link to={exportsHistoryPath}>
                <History className="h-3.5 w-3.5" />
                History
              </Link>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ReportFilterPanel ───────────────────────────────────────────────────────

export function ReportFilterPanel({ hook, issueCount }: ReportFilterPanelProps) {
  const {
    filters,
    toggleDomainFilter,
    toggleAreaFilter,
    toggleTesterFilter,
    toggleStatus,
    setSchedule,
    setHasIssues,
    setCustomDateRange,
    filterOptions,
    isFiltered,
    resetFilters,
  } = hook;

  const { data: cognitoUsers } = useTesters();
  const cognitoUserEmails = (cognitoUsers?.testers ?? []).map((u: { email: string }) => u.email).filter(Boolean).sort();

  // Build Cognito user options. Sublabel shows the mapped test-person name
  // when a USER_MAPPING exists for this project — purely informational,
  // not a gate. The lookup is case-insensitive: see findTesterNameByEmail
  // for rationale + unit tests.
  const cognitoUserOptions = cognitoUserEmails.map((email: string) => ({
    value: email,
    label: email,
    sublabel: findTesterNameByEmail(filterOptions.testerEmails, email),
  }));

  const activeFilters = getActiveFilters(hook);

  // Toggling a Cognito user adds/removes its mapped test-person name when
  // we have one; otherwise it falls back to the email itself, which the
  // backend resolves through USER_MAPPING (reports_project handler). The
  // email lookup is case-insensitive — see findTesterNameByEmail.
  const handleCognitoUserToggle = (email: string) => {
    const testerName = findTesterNameByEmail(
      filterOptions.testerEmails,
      email,
    );
    toggleTesterFilter(testerName ?? email);
  };

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      {/* Filter controls grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {/* Domain (multi-select) */}
            <SearchableMultiSelect
              label="Domain"
              placeholder="All Domains"
              searchPlaceholder="Search domains..."
              selected={filters.domain}
              options={filterOptions.domains.map((d) => ({ value: d, label: d }))}
              onToggle={toggleDomainFilter}
            />

            {/* Area (multi-select) */}
            <SearchableMultiSelect
              label="Area"
              placeholder="All Areas"
              searchPlaceholder="Search areas..."
              selected={filters.area}
              options={filterOptions.areas.map((a) => ({ value: a, label: a }))}
              onToggle={toggleAreaFilter}
            />

            {/* Status (multi-select) */}
            <SearchableMultiSelect
              label="Status"
              placeholder="All Statuses"
              searchPlaceholder="Search statuses..."
              selected={filters.status}
              options={[
                { value: 'unassigned', label: 'Unassigned' },
                ...(Object.entries(STATUS_CONFIG) as [string, { label: string }][]).map(
                  ([key, cfg]) => ({ value: key, label: cfg.label }),
                ),
              ]}
              onToggle={toggleStatus}
            />

            {/* Cognito User (multi-select by email). */}
            {cognitoUserEmails.length > 0 && (
              <SearchableMultiSelect
                label={<><User className="h-3 w-3 inline mr-1" />Cognito User</>}
                placeholder="All Users"
                searchPlaceholder="Search users..."
                selected={cognitoUserOptions
                  .filter((o) =>
                    filters.tester.includes(o.sublabel ?? o.value),
                  )
                  .map((o) => o.value)}
                options={cognitoUserOptions}
                onToggle={handleCognitoUserToggle}
              />
            )}

            {/* Issues toggle */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Issues</label>
              <Button
                variant={filters.hasIssues ? 'default' : 'outline'}
                size="sm"
                className="h-9 w-full gap-1.5"
                onClick={() => setHasIssues(!filters.hasIssues)}
              >
                With Issues
                {issueCount > 0 && (
                  <Badge
                    variant={filters.hasIssues ? 'secondary' : 'outline'}
                    className="ml-0.5 px-1.5 py-0 h-5 text-xs"
                  >
                    {issueCount}
                  </Badge>
                )}
              </Button>
            </div>


            {/* Schedule */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Schedule</label>
              <SearchableSelect
                value={filters.schedule}
                onValueChange={(v) => setSchedule(v as ScheduleFilter)}
                options={SCHEDULE_OPTIONS}
                placeholder="All dates"
                searchPlaceholder="Search schedule..."
                className="h-9"
              />
            </div>

          </div>

      {/* Custom date range picker */}
      {filters.schedule === 'custom' && (
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  'h-9 gap-2 text-left font-normal',
                  !filters.customDateRange.from && 'text-muted-foreground',
                )}
              >
                <CalendarIcon className="h-4 w-4" />
                {filters.customDateRange.from ? (
                  filters.customDateRange.to ? (
                    <>
                      {format(filters.customDateRange.from, 'dd MMM yyyy')} -{' '}
                      {format(filters.customDateRange.to, 'dd MMM yyyy')}
                    </>
                  ) : (
                    format(filters.customDateRange.from, 'dd MMM yyyy')
                  )
                ) : (
                  'Pick a date range'
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={
                  filters.customDateRange.from
                    ? {
                        from: filters.customDateRange.from,
                        to: filters.customDateRange.to,
                      }
                    : undefined
                }
                onSelect={(range) =>
                  setCustomDateRange({ from: range?.from, to: range?.to })
                }
                numberOfMonths={2}
                weekStartsOn={1}
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Active filter chips + clear */}
      {isFiltered && (
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t">
          <span className="text-xs text-muted-foreground mr-1">Active:</span>
          {activeFilters.map((f) => (
            <FilterChip key={f.key} label={f.label} value={f.value} onClear={f.onClear} />
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={resetFilters}
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
}
